import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Box, Button, Typography, TextField, InputAdornment, Chip, Card, CardContent,
  Grid, IconButton, Avatar, Tooltip,
  Snackbar, Alert, Menu, MenuItem, Checkbox, Divider, ListItemText, TableSortLabel, TablePagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SummaryCard from './SummaryCard';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArchiveIcon from '@mui/icons-material/Archive';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { truckService } from '../api/api';
import AddTruckDialog from './AddTruckDialog';
import EditTruckDialog from './EditTruckDialog';
import { isFavoriteTruck, recordTruckRecent, toggleFavoriteTruck } from '../utils/truckPrefs';
import { VEHICLE_CATEGORIES, getVehicleCategoryId } from '../utils/vehicleCategories';
import ConfirmDialog from './ConfirmDialog';

const LAST_VIEWED_TRUCK_KEY = 'biofleet_last_viewed_truck_id_v1';
const TRUCK_RETURN_PATH_KEY = 'biofleet_truck_return_path_v1';

function getStatusChipSx(status) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'active') return { backgroundColor: '#c8e6c9', color: '#2e7d32' };
  if (normalized === 'maintenance') return { backgroundColor: '#fff3e0', color: '#e65100' };
  if (normalized === 'out of service') return { backgroundColor: '#ffebee', color: '#c62828' };
  if (normalized === 'inactive') return { backgroundColor: '#eeeeee', color: '#616161' };
  return { backgroundColor: '#e3f2fd', color: '#1565c0' };
}

function normalizeRowColor(value) {
  const color = String(value || '').trim();
  return /^#[0-9a-f]{6}$/i.test(color) ? color : '';
}

function hexToRgba(hex, alpha) {
  const color = normalizeRowColor(hex);
  if (!color) return `rgba(100, 116, 139, ${alpha})`;
  const value = color.slice(1);
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function getCategoryRowColors(categoryId) {
  const palette = {
    fiveTonnes: { row: '#f8fbff', hover: '#eef6ff', border: '#93c5fd' },
    partsTruck: { row: '#f7feff', hover: '#ecfeff', border: '#67e8f9' },
    midSizeBox: { row: '#fbfcfe', hover: '#f6f8fb', border: '#cbd5e1' },
    vans: { row: '#fcf9ff', hover: '#f7f0ff', border: '#d8b4fe' },
    trailers: { row: '#fffdf5', hover: '#fff8e1', border: '#fcd34d' },
    tractors: { row: '#f7fffd', hover: '#ecfdf5', border: '#5eead4' },
    forklifts: { row: '#fffef4', hover: '#fefce8', border: '#fde68a' },
    skidsteer: { row: '#fff8f6', hover: '#fff1ed', border: '#fdba74' },
  };

  return palette[categoryId] || palette.midSizeBox;
}

function fieldMatchesVehicleSearch(value, query) {
  if (!query) return true;
  if (value === null || value === undefined) return false;

  const text = String(value).toLowerCase();
  const unitCodeSearch = /^[a-z]+\d+$/i.test(query);
  if (!unitCodeSearch) return text.includes(query);

  const tokens = text.match(/[a-z0-9]+/g) || [];
  return text === query || tokens.includes(query);
}

const TRUCK_TABLE_COLUMNS_STORAGE_KEY = 'truckTable.visibleColumns.v1';

const TRUCK_TABLE_COLUMNS = [
  { id: 'truck', label: 'Vehicle', required: true },
  { id: 'makeModel', label: 'Make / Model', required: true },
  { id: 'category', label: 'Category', defaultVisible: true },
  { id: 'year', label: 'Year', defaultVisible: true, align: 'center' },
  { id: 'plate', label: 'Plate', defaultVisible: true },
  { id: 'status', label: 'Status', defaultVisible: true, align: 'center' },
  { id: 'odometer', label: 'Odometer', defaultVisible: true, align: 'right' },
  { id: 'vehicle_type', label: 'Type' },
  { id: 'trim', label: 'Trim' },
  { id: 'vin', label: 'VIN' },
  { id: 'assigned_to', label: 'Assigned To' },
];

function getDefaultVisibleColumnIds() {
  return TRUCK_TABLE_COLUMNS
    .filter((c) => c.required || c.defaultVisible)
    .map((c) => c.id);
}

function loadVisibleColumnIds() {
  try {
    const raw = localStorage.getItem(TRUCK_TABLE_COLUMNS_STORAGE_KEY);
    if (!raw) return getDefaultVisibleColumnIds();

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return getDefaultVisibleColumnIds();

    const valid = new Set(TRUCK_TABLE_COLUMNS.map((c) => c.id));
    const required = TRUCK_TABLE_COLUMNS.filter((c) => c.required).map((c) => c.id);
    const cleaned = parsed.filter((id) => valid.has(id));
    const merged = Array.from(new Set([...required, ...cleaned]));
    return merged.length ? merged : getDefaultVisibleColumnIds();
  } catch {
    return getDefaultVisibleColumnIds();
  }
}

function TruckRow({ truck, visibleColumnIds, onEdit, onDelete, onArchive, onToggleFavorite, dragProps, isDragOver, isRecentlyViewed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const categoryId = getVehicleCategoryId(truck);
  const categoryColors = getCategoryRowColors(categoryId);
  const customRowColor = normalizeRowColor(truck.row_color);
  const normalRowColor = customRowColor || categoryColors.row;
  const hoverRowColor = customRowColor || categoryColors.hover;
  const accentRowColor = customRowColor || categoryColors.border;

  const openTruckDetails = () => {
    try {
      sessionStorage.setItem(LAST_VIEWED_TRUCK_KEY, String(truck.id));
      sessionStorage.setItem(TRUCK_RETURN_PATH_KEY, `${location.pathname}${location.search || ''}`);
    } catch {
      // ignore
    }
    recordTruckRecent(truck.id);
    navigate(`/truck/${truck.id}`);
  };

  const renderCell = (colId) => {
    switch (colId) {
      case 'truck':
        return (
          <TableCell key={colId}>
            {colId === 'truck' && (
              <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', mr: 1 }}>
                <Tooltip title="Drag to manually order">
                  <DragIndicatorIcon
                    sx={{ color: '#94a3b8', cursor: 'grab', flexShrink: 0, '&:active': { cursor: 'grabbing' } }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Box>
            )}
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#001f3f' }}>
              {truck.name || '—'}
            </Typography>
            {isRecentlyViewed && (
              <Chip
                size="small"
                label="Recently viewed"
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 900,
                  bgcolor: normalRowColor,
                  color: '#001f3f',
                  border: `1px solid ${hexToRgba(accentRowColor, 0.42)}`,
                }}
              />
            )}
            <Typography variant="caption" color="textSecondary">
              ID: {truck.id}
            </Typography>
          </TableCell>
        );
      case 'makeModel':
        return (
          <TableCell key={colId}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#001f3f' }}>
              {truck.make || '—'} {truck.model || ''}
            </Typography>
            {truck.trim && (
              <Typography variant="caption" color="textSecondary">
                {truck.trim}
              </Typography>
            )}
          </TableCell>
        );
      case 'category': {
        const cid = getVehicleCategoryId(truck);
        const label = VEHICLE_CATEGORIES.find((c) => c.id === cid)?.label || '—';
        return (
          <TableCell key={colId}>
            {cid === 'partsTruck' ? (
              <Chip
                size="small"
                icon={<LocalShippingIcon sx={{ fontSize: '16px !important' }} />}
                label="Parts Truck Inventory"
                sx={{
                  bgcolor: '#dbeafe',
                  color: '#075985',
                  border: '1px solid #0284c755',
                  fontWeight: 900,
                  '& .MuiChip-icon': { color: '#075985' },
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#001f3f' }}>
                {label}
              </Typography>
            )}
          </TableCell>
        );
      }
      case 'year':
        return (
          <TableCell key={colId} align="center">
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#001f3f' }}>
              {truck.year || '—'}
            </Typography>
          </TableCell>
        );
      case 'plate':
        return (
          <TableCell key={colId}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {truck.license_plate || '—'}
            </Typography>
          </TableCell>
        );
      case 'status':
        return (
          <TableCell key={colId} align="center">
            <Chip
              label={truck.status || '—'}
              size="small"
              sx={{
                ...getStatusChipSx(truck.status),
                fontWeight: 'bold',
              }}
            />
          </TableCell>
        );
      case 'odometer':
        return (
          <TableCell key={colId} align="right">
            {truck.odometer ? (
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#001f3f' }}>
                {truck.odometer.toLocaleString()}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
                —
              </Typography>
            )}
          </TableCell>
        );
      case 'vehicle_type':
        return (
          <TableCell key={colId}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#001f3f' }}>
              {truck.vehicle_type || '—'}
            </Typography>
          </TableCell>
        );
      case 'trim':
        return (
          <TableCell key={colId}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#001f3f' }}>
              {truck.trim || '—'}
            </Typography>
          </TableCell>
        );
      case 'vin':
        return (
          <TableCell key={colId}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#001f3f' }}>
              {truck.vin || '—'}
            </Typography>
          </TableCell>
        );
      case 'assigned_to':
        return (
          <TableCell key={colId}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: '#001f3f',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 220,
              }}
              title={truck.assigned_to || ''}
            >
              {truck.assigned_to || '—'}
            </Typography>
          </TableCell>
        );
      default:
        return null;
    }
  };

  return (
      <TableRow
        draggable
        onDragStart={dragProps?.onDragStart}
        onDragOver={dragProps?.onDragOver}
        onDragLeave={dragProps?.onDragLeave}
        onDrop={dragProps?.onDrop}
        onDragEnd={dragProps?.onDragEnd}
        sx={{
          '& > *': { borderBottom: 'unset' },
          backgroundColor: isDragOver ? '#fff8e1' : normalRowColor,
          borderLeft: isDragOver ? '6px solid #f59e0b' : `6px solid ${accentRowColor}`,
          boxShadow: isRecentlyViewed ? `inset 0 0 0 2px ${hexToRgba(accentRowColor, 0.28)}, 0 6px 18px ${hexToRgba(accentRowColor, 0.14)}` : 'none',
          '&:hover': { backgroundColor: hoverRowColor },
          cursor: 'pointer',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        }}
        onClick={openTruckDetails}
      >
        {visibleColumnIds.map((colId) => renderCell(colId))}
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Open details">
              <IconButton
                size="small"
                onClick={openTruckDetails}
                sx={{ color: '#2e7d32', '&:hover': { backgroundColor: '#e8f5e9' } }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={isFavoriteTruck(truck.id) ? 'Unfavorite' : 'Favorite'}>
              <IconButton
                size="small"
                onClick={() => onToggleFavorite(truck.id)}
                sx={{ color: isFavoriteTruck(truck.id) ? '#fbc02d' : '#616161', '&:hover': { backgroundColor: '#fffde7' } }}
              >
                {isFavoriteTruck(truck.id) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => onEdit(truck)}
                sx={{ color: '#1976d2', '&:hover': { backgroundColor: '#e3f2fd' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Archive">
              <IconButton
                size="small"
                onClick={() => onArchive(truck.id)}
                sx={{ color: '#455a64', '&:hover': { backgroundColor: '#eceff1' } }}
              >
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Remove safely (recoverable)">
              <IconButton
                size="small"
                onClick={() => onDelete(truck.id)}
                sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
  );
}

export default function TrucksTable({ categoryId = null, pageTitle = 'Vehicles', pageSubtitle = 'Fleet specifications and status overview' }) {
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const [showArchived, setShowArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [archiveConfirm, setArchiveConfirm] = useState({ open: false, truckId: null, label: '' });
  const [archiving, setArchiving] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState(() => loadVisibleColumnIds());
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const [sortState, setSortState] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [draggedTruckId, setDraggedTruckId] = useState(null);
  const [dragOverTruckId, setDragOverTruckId] = useState(null);
  const [recentlyViewedTruckId, setRecentlyViewedTruckId] = useState(() => {
    try {
      const stored = Number(sessionStorage.getItem(LAST_VIEWED_TRUCK_KEY));
      return Number.isFinite(stored) ? stored : null;
    } catch {
      return null;
    }
  });
  const dragOverTruckIdRef = useRef(null);
  const navigate = useNavigate();

  const fetchRows = useCallback(async () => {
    try {
      const res = await truckService.getAll(showArchived ? { includeArchived: 1 } : undefined);
      setRows(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    try {
      const stored = Number(sessionStorage.getItem(LAST_VIEWED_TRUCK_KEY));
      setRecentlyViewedTruckId(Number.isFinite(stored) ? stored : null);
    } catch {
      setRecentlyViewedTruckId(null);
    }
  }, [rows]);

  useEffect(() => {
    const handler = () => fetchRows();
    window.addEventListener('vehiclesUpdated', handler);
    window.addEventListener('dataUpdated', handler);
    return () => {
      window.removeEventListener('vehiclesUpdated', handler);
      window.removeEventListener('dataUpdated', handler);
    };
  }, [fetchRows]);

  const handleTruckAdded = (newTruck) => {
    setRows((prev) => [...prev, newTruck]);
    recordTruckRecent(newTruck?.id);
    window.dispatchEvent(new Event('vehiclesUpdated'));
    window.dispatchEvent(new Event('dataUpdated'));
  };

  const handleEditTruck = (truck) => {
    setSelectedTruck(truck);
    setEditDialogOpen(true);
  };

  const handleTruckUpdated = (updatedTruck) => {
    setRows((prev) => prev.map((t) => (t.id === updatedTruck.id ? updatedTruck : t)));
    recordTruckRecent(updatedTruck?.id);
    window.dispatchEvent(new Event('vehiclesUpdated'));
    window.dispatchEvent(new Event('dataUpdated'));
  };

  const handleToggleFavorite = (truckId) => {
    const wasFavorite = isFavoriteTruck(truckId);
    toggleFavoriteTruck(truckId);
    setToast({ open: true, severity: 'success', message: wasFavorite ? 'Removed from favorites.' : 'Added to favorites.' });
  };

  const handleArchiveTruck = async (id) => {
    const t = rows.find((x) => x.id === id);
    setArchiveConfirm({
      open: true,
      truckId: id,
      label: t?.name || `Truck #${id}`,
    });
  };

  const confirmArchive = async () => {
    if (!archiveConfirm.truckId) return;
    try {
      setArchiving(true);
      const res = await truckService.archive(archiveConfirm.truckId);
      const archivedPartCount = Number(res.data?.archived_parts_count || 0);
      setRows((prev) => prev.filter((t) => t.id !== archiveConfirm.truckId));
      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setToast({
        open: true,
        severity: 'success',
        message: archivedPartCount
          ? `Archived ${archiveConfirm.label} and ${archivedPartCount} attached part record${archivedPartCount === 1 ? '' : 's'}.`
          : `Archived ${archiveConfirm.label}.`,
      });
    } catch (err) {
      console.error("Error archiving truck:", err);
      const message = err?.response?.data?.error || err?.message || 'Failed to archive truck.';
      setToast({ open: true, severity: 'error', message });
    } finally {
      setArchiving(false);
      setArchiveConfirm({ open: false, truckId: null, label: '' });
    }
  };

  const handleDeleteTruck = async (id) => {
    const confirmDelete = window.confirm("Remove this truck from the active list? It will be preserved in the recovery archive and can be restored.");
    if (!confirmDelete) return;

    try {
      await truckService.delete(id);
      setRows((prev) => prev.filter((t) => t.id !== id));
      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (err) {
      console.error("Error moving truck to recovery archive:", err);
      const message = err?.response?.data?.error || err?.message || 'Failed to move truck to the recovery archive.';
      setToast({ open: true, severity: 'error', message });
    }
  };

  const baseRows = useMemo(() => {
    if (!categoryId) return rows;
    return rows.filter((t) => getVehicleCategoryId(t) === categoryId);
  }, [rows, categoryId]);

  const statusFilteredRows = useMemo(() => {
    if (statusFilter === 'all') return baseRows;
    return baseRows.filter((t) => String(t.status || '').trim().toLowerCase() === statusFilter);
  }, [baseRows, statusFilter]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return statusFilteredRows;
    return statusFilteredRows.filter((t) => [
      t.name,
      t.make,
      t.model,
      t.license_plate,
      t.plate,
      t.serial_number,
      t.vin,
      t.status,
      t.id,
    ].some((value) => fieldMatchesVehicleSearch(value, q)));
  }, [statusFilteredRows, searchTerm]);

  useEffect(() => {
    try {
      localStorage.setItem(TRUCK_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumnIds));
    } catch {
      // ignore
    }
  }, [visibleColumnIds]);

  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    if (!sortState?.key) {
      return data.sort((a, b) => {
        const av = Number(a?.display_order || 0);
        const bv = Number(b?.display_order || 0);
        if (av !== bv) return av - bv;
        return Number(a?.id || 0) - Number(b?.id || 0);
      });
    }

    const dir = sortState.direction === 'asc' ? 1 : -1;

    data.sort((a, b) => {
      if (sortState.key === 'year') {
        const av = Number(a?.year || 0);
        const bv = Number(b?.year || 0);
        return (av - bv) * dir;
      }
      if (sortState.key === 'odometer') {
        const av = a?.odometer === null || a?.odometer === undefined || a?.odometer === '' ? Number.POSITIVE_INFINITY : Number(a.odometer);
        const bv = b?.odometer === null || b?.odometer === undefined || b?.odometer === '' ? Number.POSITIVE_INFINITY : Number(b.odometer);
        return (av - bv) * dir;
      }
      return 0;
    });

    return data;
  }, [filteredRows, sortState]);

  const paginatedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage],
  );

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, categoryId, showArchived]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [page, rowsPerPage, sortedRows.length]);

  const toggleColumn = (colId) => {
    const meta = TRUCK_TABLE_COLUMNS.find((c) => c.id === colId);
    if (!meta || meta.required) return;

    setVisibleColumnIds((prev) => {
      const has = prev.includes(colId);
      const next = has ? prev.filter((id) => id !== colId) : [...prev, colId];
      const requiredIds = TRUCK_TABLE_COLUMNS.filter((c) => c.required).map((c) => c.id);
      const merged = Array.from(new Set([...requiredIds, ...next]));
      return merged.length ? merged : getDefaultVisibleColumnIds();
    });
  };

  const handleSortToggle = (key) => {
    setSortState((prev) => {
      if (prev?.key !== key) return { key, direction: 'asc' };
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const persistVehicleOrder = async (orderedIds) => {
    try {
      await truckService.reorder(orderedIds);
      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setToast({ open: true, severity: 'success', message: 'Vehicle order saved.' });
    } catch (err) {
      console.error('Error saving vehicle order:', err);
      const message = err?.response?.data?.error || err?.message || 'Failed to save vehicle order.';
      setToast({ open: true, severity: 'error', message });
      fetchRows();
    }
  };

  const handleDragStart = (truckId) => (event) => {
    setSortState(null);
    setDraggedTruckId(truckId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(truckId));
  };

  const handleDragOver = (truckId) => (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverTruckIdRef.current !== truckId) {
      dragOverTruckIdRef.current = truckId;
      setDragOverTruckId(truckId);
    }
  };

  const handleDrop = (targetTruckId) => async (event) => {
    event.preventDefault();
    const sourceTruckId = draggedTruckId || parseInt(event.dataTransfer.getData('text/plain'), 10);
    setDraggedTruckId(null);
    dragOverTruckIdRef.current = null;
    setDragOverTruckId(null);

    if (!sourceTruckId || sourceTruckId === targetTruckId) return;

    const currentIds = sortedRows.map((truck) => truck.id);
    const fromIndex = currentIds.indexOf(sourceTruckId);
    const toIndex = currentIds.indexOf(targetTruckId);
    if (fromIndex < 0 || toIndex < 0) return;

    const nextVisibleIds = [...currentIds];
    const [movedId] = nextVisibleIds.splice(fromIndex, 1);
    nextVisibleIds.splice(toIndex, 0, movedId);

    const visibleSet = new Set(currentIds);
    const fullManualIds = [...rows]
      .sort((a, b) => {
        const av = Number(a?.display_order || 0);
        const bv = Number(b?.display_order || 0);
        if (av !== bv) return av - bv;
        return Number(a?.id || 0) - Number(b?.id || 0);
      })
      .map((truck) => truck.id);
    const firstVisibleIndex = Math.max(0, fullManualIds.findIndex((id) => visibleSet.has(id)));
    const remainingIds = fullManualIds.filter((id) => !visibleSet.has(id));
    const nextIds = [
      ...remainingIds.slice(0, firstVisibleIndex),
      ...nextVisibleIds,
      ...remainingIds.slice(firstVisibleIndex),
    ];

    setRows((prev) => {
      const orderMap = new Map(nextIds.map((id, index) => [id, index + 1]));
      return prev.map((truck) => (
        orderMap.has(truck.id) ? { ...truck, display_order: orderMap.get(truck.id) } : truck
      ));
    });

    await persistVehicleOrder(nextIds);
  };

  const handleDragEnd = () => {
    setDraggedTruckId(null);
    dragOverTruckIdRef.current = null;
    setDragOverTruckId(null);
  };

  const statusCounts = useMemo(() => {
    const counts = { active: 0, maintenance: 0, inactive: 0, out: 0 };
    baseRows.forEach((t) => {
      const s = (t.status || '').toLowerCase();
      if (s === 'active') counts.active += 1;
      else if (s === 'maintenance') counts.maintenance += 1;
      else if (s === 'inactive') counts.inactive += 1;
      else if (s === 'out of service') counts.out += 1;
    });
    return counts;
  }, [baseRows]);


  const handleExportTrucks = async () => {
    try {
      const res = await api.get('/export/trucks', { responseType: 'blob', timeout: 120000 });
      const disposition = res.headers?.['content-disposition'] || '';
      const match = String(disposition).match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || 'biofleet_vehicles_export.zip';
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting vehicles zip:', err);
      setToast({ open: true, severity: 'error', message: err?.response?.data?.error || err?.message || 'Failed to export vehicles.' });
    }
  };

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, minHeight: '100dvh' }}>
      <Card className="portal-page-header" sx={{
        mb: 1.25,
        background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
        color: 'white',
        borderRadius: 2.25,
        boxShadow: '0 6px 24px rgba(0, 31, 63, 0.26)'
      }}>
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 }, flexWrap: 'wrap' }}>
            <Avatar sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              backgroundColor: '#00ff97',
              boxShadow: '0 3px 14px rgba(0, 255, 151, 0.35)'
            }}>
              <DirectionsCarIcon sx={{ fontSize: { xs: 23, sm: 27 }, color: '#001f3f' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.1, fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05 }}>
                {pageTitle}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                {pageSubtitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  backgroundColor: '#00ff97',
                  color: '#001f3f',
                  fontWeight: 'bold',
                  px: 1.55,
                  py: 0.62,
                  minHeight: 36,
                  borderRadius: 1.6,
                  fontSize: { xs: '0.78rem', sm: '0.86rem' },
                  '&:hover': {
                    backgroundColor: '#00cc7a',
                    boxShadow: '0 4px 20px rgba(0, 255, 151, 0.4)'
                  }
                }}
              >
                Add Vehicle
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportTrucks}
                sx={{
                  borderColor: '#00ff97',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 1.55,
                  py: 0.62,
                  minHeight: 36,
                  borderRadius: 1.6,
                  fontSize: { xs: '0.78rem', sm: '0.86rem' },
                  '&:hover': {
                    borderColor: '#00cc7a',
                    backgroundColor: 'rgba(0, 255, 151, 0.1)'
                  }
                }}
              >
                Export Trucks
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={1} sx={{ mb: 1.75 }}>
        {[
          { label: 'Total Vehicles', value: baseRows.length, accent: '#3b658b', tint: '#c6dff7', icon: <LocalShippingIcon />, filter: 'all' },
          { label: 'Active', value: statusCounts.active, accent: '#356d59', tint: '#c4e8d1', icon: <DirectionsCarIcon />, filter: 'active' },
          { label: 'Maintenance', value: statusCounts.maintenance, accent: '#866523', tint: '#f9dfa4', icon: 'MAINT', filter: 'maintenance' },
          { label: 'Out of Service', value: statusCounts.out, accent: '#87516d', tint: '#f3cde2', icon: 'OOS', filter: 'out of service' },
        ].map((item) => (
          <Grid size={{ xs: 6, md: 3 }} key={item.label}>
            <SummaryCard {...item} selected={!item.unselected && statusFilter === item.filter} onClick={() => setStatusFilter(item.filter)} />
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, make, model, plate, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={fetchRows}
              startIcon={<RefreshIcon />}
              sx={{
                minWidth: { xs: '100%', sm: 140 },
                height: 56,
                background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #003d7a 0%, #001f3f 100%)',
                }
              }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
              startIcon={<ViewColumnIcon />}
              sx={{ minWidth: { xs: '100%', sm: 140 }, height: 56 }}
            >
              Columns
            </Button>
          </Box>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="textSecondary">
              Showing {filteredRows.length} of {baseRows.length} vehicles
            </Typography>
            {statusFilter !== 'all' && (
              <Chip
                size="small"
                label={`Status: ${statusFilter}`}
                onDelete={() => setStatusFilter('all')}
                sx={{ fontWeight: 900, bgcolor: '#e3f2fd', color: '#1565c0' }}
              />
            )}
            <Chip
              size="small"
              icon={<DragIndicatorIcon sx={{ fontSize: '16px !important' }} />}
              label="Drag rows to save manual order"
              sx={{ fontWeight: 900, bgcolor: '#fff8e1', color: '#92400e', border: '1px solid #f59e0b55' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
              }
              label="Include archived"
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              clickable
              label="All"
              variant={!categoryId ? 'filled' : 'outlined'}
              color={!categoryId ? 'primary' : 'default'}
              onClick={() => navigate('/trucks')}
              sx={{ fontWeight: 800 }}
            />
            {VEHICLE_CATEGORIES.map((c) => (
              <Chip
                key={c.id}
                clickable
                label={c.label}
                variant={categoryId === c.id ? 'filled' : 'outlined'}
                color={categoryId === c.id ? 'primary' : 'default'}
                onClick={() => navigate(`/trucks/${c.slug}`)}
                sx={{ fontWeight: 800 }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        flexWrap: 'wrap',
        border: '1px solid #d8e2ef'
      }}>
        <Typography variant="body2" sx={{ fontWeight: 900, color: '#001f3f', mr: 0.5 }}>
          Vehicle row colors by category:
        </Typography>
        {VEHICLE_CATEGORIES.map((category) => {
          const colors = getCategoryRowColors(category.id);
          return (
            <Chip
              key={category.id}
              size="small"
              icon={<LocalShippingIcon sx={{ fontSize: '16px !important' }} />}
              label={category.label}
              sx={{
                bgcolor: colors.row,
                color: '#001f3f',
                fontWeight: 900,
                border: `1px solid ${colors.border}66`,
                '& .MuiChip-icon': { color: colors.border },
              }}
            />
          );
        })}
        <Chip
          size="small"
          label="Custom color overrides category"
          sx={{ bgcolor: '#ffffff', color: '#001f3f', fontWeight: 900, border: '1px dashed #64748b' }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)' }}>
              {visibleColumnIds.map((colId) => {
                const meta = TRUCK_TABLE_COLUMNS.find((c) => c.id === colId);
                const align = meta?.align || 'left';

                if (colId === 'year' || colId === 'odometer') {
                  const active = sortState?.key === colId;
                  return (
                    <TableCell
                      key={colId}
                      align={align}
                      sortDirection={active ? sortState.direction : false}
                      sx={{ color: 'white', fontWeight: 'bold', fontSize: 14, whiteSpace: 'nowrap' }}
                    >
                      <TableSortLabel
                        active={active}
                        direction={active ? sortState.direction : 'asc'}
                        onClick={() => handleSortToggle(colId)}
                        sx={{
                          color: 'white',
                          '&.Mui-active': { color: 'white' },
                          '&:hover': { color: 'white' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        {meta?.label || colId}
                      </TableSortLabel>
                    </TableCell>
                  );
                }

                return (
                  <TableCell
                    key={colId}
                    align={align}
                    sx={{ color: 'white', fontWeight: 'bold', fontSize: 14, whiteSpace: 'nowrap' }}
                  >
                    {meta?.label || colId}
                  </TableCell>
                );
              })}
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((truck) => (
              <TruckRow
                key={truck.id}
                truck={truck}
                visibleColumnIds={visibleColumnIds}
                onEdit={handleEditTruck}
                onDelete={handleDeleteTruck}
                onArchive={handleArchiveTruck}
                onToggleFavorite={handleToggleFavorite}
                isDragOver={dragOverTruckId === truck.id}
                isRecentlyViewed={recentlyViewedTruckId === Number(truck.id)}
                dragProps={{
                  onDragStart: handleDragStart(truck.id),
                  onDragOver: handleDragOver(truck.id),
                  onDragLeave: () => setDragOverTruckId((current) => (current === truck.id ? null : current)),
                  onDrop: handleDrop(truck.id),
                  onDragEnd: handleDragEnd,
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={Paper}
        count={sortedRows.length}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100, 150]}
        sx={{ mt: 1, borderRadius: 2 }}
      />

      <Menu
        anchorEl={columnsAnchorEl}
        open={Boolean(columnsAnchorEl)}
        onClose={() => setColumnsAnchorEl(null)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Table Columns
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Choose the fields you want to see.
          </Typography>
        </Box>
        <Divider />
        {TRUCK_TABLE_COLUMNS.filter((c) => !c.required).map((col) => (
          <MenuItem key={col.id} dense onClick={() => toggleColumn(col.id)}>
            <Checkbox checked={visibleColumnIds.includes(col.id)} />
            <ListItemText primary={col.label} />
          </MenuItem>
        ))}
      </Menu>

      <AddTruckDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onTruckAdded={handleTruckAdded}
      />

      <EditTruckDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        truck={selectedTruck}
        onTruckUpdated={handleTruckUpdated}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={archiveConfirm.open}
        title="Archive Truck?"
        description={`Archive ${archiveConfirm.label}? This hides it from primary views but keeps it in the database.`}
        confirmText={archiving ? 'Archiving...' : 'Archive'}
        confirmColor="warning"
        onConfirm={confirmArchive}
        onClose={() => setArchiveConfirm({ open: false, truckId: null, label: '' })}
        loading={archiving}
        icon={<ArchiveIcon />}
      />
    </Box>
  );
}

